import { computed, defineComponent, ref } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";

import {
    ICallHistory,
    ICallHistoryRequest,
    ICallParticipant
} from "../../../resources-vue/vue-interfaces/messaging-service/i-call-history";
import {
    ICallControls,
    ICallStatusChange,
    IOutgoingCallProps
} from "../../../resources-vue/vue-interfaces/messaging-service/i-notification";
import {
    formatDateTimeToLocal,
    formatDisplayUsers,
    formatUserInitials
} from "../../../common/vue-helpers/modifiers/format-message";
import { getSolutionClassIndex } from "../../../common/vue-helpers/solution-color-index";
import { debounce } from "../../../common/vue-helpers/debounce";
import { RoomDetails } from "../../../resources-vue/vue-interfaces/messaging-service/i-message";
import { useLoadingIndicator } from "../../Common/LoadingIndicator/store/useLoadingIndicator";
import {
    AttendanceTypeEnum,
    CallType,
    CallTypeEnum,
    ChatNavOptionsEnum,
    ConversationTypeEnum,
    ConversationRoomTypeEnum,
    jitsiConferenceStatus,
    NotificationsHubEvents,
    AvaInitialsType
} from "../../../enums/enums";
import { SignalRHubs } from "../../../enums/SignalRHubs";
import { MessageService } from "../../../services/message-service";
import { SignalRService } from "../../../services/signal-r-service";
import eventBus from "../../../utilities/eventBus";
import { jitsiRoomHandler } from "../../../utilities/jitsiRoomHandler";
import { useApp } from "../../App/useApp";
import { useUserStore } from "../store/useUsersStore";
import { searchStore } from "../store/callSearch";
import { useMessagesStore } from "../store/useMessagesStore";
import { useCallStore } from "../store/useCallStore";

import ScrollPagination from "../../../resources-vue/vue-elements/ScrollPagination/ScrollPagination.vue";
import AvaInitials from "../components/AvaInitials/AvaInitials.vue";
import CallSearch from "../CallSearch/CallSearch.vue";

export default defineComponent({
    components: {
        ScrollPagination,
        AvaInitials,
        CallSearch
    },

    data() {
        return {
            _messageService: null as MessageService,
            dialogRef: ref(null),
            dialogOpened: false as boolean,
            conversationRoomDetails: null as RoomDetails,
            ConversationRoomTypeEnum: ConversationRoomTypeEnum,
            selectedConversationType: ConversationRoomTypeEnum.AudioCall,
            signalRService: null as SignalRService,
            callStatus: null,
            showJointMeetingBtn: false as boolean,
            userOs: "" as string,
            callHistoryList: null as ICallHistory[],
            isLoading: false as boolean,
            loadMore: false as boolean,
            callFilterType: null as CallTypeEnum | "All",
            pageNumber: 1 as number,
            pageLength: 25 as number,
            pageCount: null as number,
            hasError: false as boolean,
            searchTermDebounce: null,
            callControls: null as ICallControls,
            disableCallBtns: false as boolean,
            getSolutionClassIndex,
            formatUserInitials,
            formatDisplayUsers,
            formatDateTimeToLocal,
            debounce,
            CallType,
            ConversationTypeEnum,
            AvaInitialsType
        };
    },

    async mounted() {
        this.signalRService = Container.instance.get(SignalRService);
        this.callControls = {
            audioEnabled: true,
            videoEnabled: false
        };
        await this.setUpCallStatusChangeEvent();
    },

    async created() {
        this.searchTermDebounce = debounce(this.filtersUpdated, 500);
        this.userOs = window.sessionStorage.getItem("userOs");
        this._messageService = Container.instance.get(MessageService);
        eventBus.on("dialogOpened", this.handleJitsiDialogOpened);
        eventBus.on("updateCallLogsAfterDelete", this.disableConversationEvents);
        eventBus.on("retryCall", this.handleRetryCall);
        eventBus.on("updateDisableCallBtn", (response: boolean) => {
            this.disableCallBtns = response;
        });
        await this.getCallHistory();
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef.value)
        };
    },
    watch: {
        $route({ params: { id: toId } }, { params: { id: fromId } }) {
            if (toId !== fromId) {
                this.showJointMeetingBtn = this.callStatus?.conversationId === toId;
            }
        },

        "searchStore.searchTerm"(newValue, oldValue) {
            if (newValue != oldValue) {
                this.searchTermDebounce();
            }
        }
    },
    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),
        ...mapState(useCallStore, ["currentCallDetails", "outgoingCallInfo", "showCallHeader"]),
        ...mapState(useUserStore, ["getUser"]),

        allPagesLoaded(): boolean {
            return !!this.pageCount && this.pageNumber > this.pageCount;
        },

        searchStore() {
            return searchStore;
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        ...mapActions(useCallStore, ["setCurrentCallDetails", "setOutgoingCallInfo"]),
        ...mapActions(useMessagesStore, ["fetchNewerMessages"]),

        handleJitsiDialogOpened(isJitsiOpen: boolean) {
            this.dialogOpened = isJitsiOpen;
        },

        async setUpCallStatusChangeEvent() {
            try {
                this.signalRService.on(
                    SignalRHubs.NotificationsHub,
                    NotificationsHubEvents.CallStatusChange,
                    async (callStatus: ICallStatusChange) => {
                        if (callStatus.conferenceStatus === jitsiConferenceStatus.Ended) {
                            await this.getCallHistory(false);
                        }
                    }
                );
            } catch (error) {
                console.error("Error running SignalR service:", error);
            }
        },

        async sendCallNotification(roomId: string, callDetail: any, isRetryCall: boolean = false) {
            const callerName = `${this.loggedInUserDetails.firstName} ${this.loggedInUserDetails.lastName}`;
            let callMsg = `${callerName} is calling`;

            if (!isRetryCall) {
                this.setCurrentCallDetails({
                    convId: callDetail.conversationId,
                    roomId,
                    isUserOnCall: true
                });

                callMsg =
                    callDetail?.conversationType === ConversationTypeEnum.GroupConversation
                        ? callDetail?.title?.length > 0
                            ? callDetail.title
                            : formatDisplayUsers(callDetail?.users, 100)
                        : `${callerName} is calling`;
            }

            const payload = {
                subjectId: this.loggedInUserDetails.loginId,
                callerName,
                callMessage: callMsg
            };

            await this._messageService.sendCallNotification(roomId, payload);

            if (this.outgoingCallInfo.convType == ConversationTypeEnum.GroupConversation) {
                await this.getConversationRoomStatus(callDetail, false);
                await this.handleJoinRoom(roomId, callDetail, false);
                await this.markAttendanceForUser(AttendanceTypeEnum.Join, callDetail.conversationId, roomId);
                this.fetchNewerMessages(callDetail.conversationId);
            } else {
                this.handleCalling(roomId, callDetail, isRetryCall);
            }
        },

        handleCalling(roomId: string, callDetail: any, isRetryCall: boolean = false) {
            let outgoingDetails: IOutgoingCallProps;
            if (isRetryCall) {
                outgoingDetails = {
                    recipient: this.outgoingCallInfo.users[0],
                    callType: this.outgoingCallInfo.roomType,
                    conversationId: this.outgoingCallInfo.convId,
                    roomId,
                    callControls: this.callControls
                };
                this.setOutgoingCallInfo({ ...this.outgoingCallInfo, isRetryCall: true });
            } else {
                outgoingDetails = {
                    recipient: callDetail.callParticipants[0],
                    callType: this.selectedConversationType,
                    conversationId: callDetail.conversationId,
                    roomId,
                    callControls: this.callControls
                };
            }
            eventBus.emit("outgoingCallNotification", outgoingDetails);
        },

        async getConversationRoomStatus(callDetail: any, isRetryCall: boolean) {
            try {
                const conversationRoomStatus = await this._messageService.getConversationRoomStatus(
                    isRetryCall ? this.outgoingCallInfo.convId : callDetail.conversationId
                );
                this.conversationRoomDetails = conversationRoomStatus.roomDetails;
            } catch (error) {
                console.error("Error fetching conversation roomm status", error);
            }
        },

        async startMeeting(roomType: ConversationRoomTypeEnum, callDetail: any, isRetryCall: boolean = false) {
            this.disableCallBtns = isRetryCall ? false : this.disableCallBtns;
            if (!this.disableCallBtns) {
                this.disableCallBtns = true;
                this.selectedConversationType = roomType;
                if (this.selectedConversationType == ConversationRoomTypeEnum.AudioCall) {
                    this.callControls = {
                        audioEnabled: true,
                        videoEnabled: false
                    };
                } else {
                    this.callControls = {
                        audioEnabled: true,
                        videoEnabled: true
                    };
                }
                const payload = {
                    conversationId: isRetryCall ? this.outgoingCallInfo.convId : callDetail.conversationId,
                    conversationRoomType: roomType
                };
                const startRoomResponse = await this._messageService.startConversationRoom(payload);
                if (!isRetryCall) {
                    this.setOutgoingCallInfo({
                        convId: callDetail.conversationId,
                        users: callDetail.callParticipants,
                        convType: callDetail.conversationType,
                        roomType: roomType,
                        isRetryCall: false
                    });
                }
                await this.sendCallNotification(startRoomResponse.roomId, callDetail, isRetryCall);
            }
        },

        async handleJoinRoom(roomId: string, callDetail: any, isRetryCall: boolean) {
            if (isRetryCall) {
                await jitsiRoomHandler(this, roomId, this.conversationRoomDetails.startTime, this.callControls, {
                    users: this.outgoingCallInfo.users,
                    conversationRoomType: this.outgoingCallInfo.roomType
                });
            } else
                await jitsiRoomHandler(this, roomId, this.conversationRoomDetails.startTime, this.callControls, {
                    users: callDetail.callParticipants,
                    conversationRoomType: this.selectedConversationType
                });
        },

        handleRetryCall() {
            if (this.$route?.query?.tab === ChatNavOptionsEnum.Call) {
                this.setCurrentCallDetails({
                    convId: this.currentCallDetails.convId,
                    roomId: this.currentCallDetails.roomId,
                    isUserOnCall: true
                });
                this.startMeeting(this.outgoingCallInfo.roomType, this.outgoingCallInfo, true);
            }
        },

        async getCallHistory(showLoading: boolean = true) {
            try {
                if (showLoading) {
                    this.isLoading = true;
                }
                const params: ICallHistoryRequest = {
                    page: this.pageNumber,
                    pageLength: this.pageLength,
                    callHistoryType: this.callFilterType == "All" ? null : this.callFilterType,
                    searchTerm: searchStore.searchTerm
                };
                const response = await this._messageService.getCallInfo(params);
                this.callHistoryList = response.calls;
                this.pageCount = response.pageCount;
            } catch (e) {
                console.error(e);
                this.hasError = true;
            } finally {
                this.isLoading = false;
            }
        },

        async updateCallHistory() {
            this.loadMore = true;
            const params: ICallHistoryRequest = {
                page: this.pageNumber,
                pageLength: this.pageLength,
                callHistoryType: this.callFilterType == "All" ? null : this.callFilterType,
                searchTerm: searchStore.searchTerm
            };
            const result = await this._messageService.getCallInfo(params);
            this.loadMore = false;
            this.callHistoryList = result.calls ? this.callHistoryList.concat(result.calls) : this.callHistoryList;
            this.pageCount = result.pageCount;
        },

        getInitials(participants: ICallParticipant[]) {
            return participants.find((user: ICallParticipant) => !user.isCurrentParticipant);
        },

        filtersUpdated() {
            this.pageNumber = 1;
            this.getCallHistory();
        },

        pageNumberUpdated(pageToLoad: number) {
            if (!pageToLoad) {
                return;
            }
            if (pageToLoad != this.pageNumber) {
                this.pageNumber = pageToLoad;
            } else {
                return;
            }
            if (!this.allPagesLoaded) {
                this.updateCallHistory();
            }
        },

        navigateToConversation(id: string) {
            this.$router.push({
                path: `/central-messaging/clinicians/conversation/${id}`,
                query: { tab: ChatNavOptionsEnum.Chat },
                replace: true,
                force: true
            });
            eventBus.emit("updateActiveChatNav", ChatNavOptionsEnum.Chat);
        },

        getCallParticipants(participants: ICallParticipant[]) {
            return participants.filter((participant) => participant.isCurrentParticipant == false);
        },

        filterCallLog(callType: CallTypeEnum | "All") {
            if (this.callFilterType == callType) return;
            this.callFilterType = callType;
            this.filtersUpdated();
        },

        disableConversationEvents(conversationId: string) {
            for (const history of this.callHistoryList) {
                if (history.conversationId === conversationId) {
                    history.isDeletedParticipant = true;
                }
            }
        }
    },

    beforeUnmount() {
        eventBus.off("dialogOpened", this.handleJitsiDialogOpened);
        eventBus.off("updateCallLogsAfterDelete", this.disableConversationEvents);
    }
});
